import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePrestamos } from "../hooks/usePrestamos";
import { useCuentas } from "../../../settings/hooks/useSettings";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import ConfirmModal from "../../shared/ConfirmModal";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import fs from "../../shared/fichas.module.css";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const MONEDAS = ["CRC", "USD"];
const MONEDA_SYMBOL = { CRC: "₡", USD: "$" };
const EMPTY_PRESTAMO = {
  nombre: "", entidad_financiera: "", monto_inicial: "", moneda: "CRC",
  tipo_cambio: "1", tasa_interes: "", fecha_inicio: "", fecha_vencimiento: "",
  cuota_mensual: "", cuenta_bancaria: "",
};
const EMPTY_PAGO = { fecha_pago: "", monto_capital: "", monto_interes: "", documento: "" };

/** "2026-01-15" → "15/01/2026" (tolera undefined/inválido) */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso || "-";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ─────────────────────────────────────────────────────────
// Estilos compartidos por los modales (mismo patrón que EmitirNotaCreditoModal.js)
// ─────────────────────────────────────────────────────────
const OVERLAY = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  zIndex: 1000, backdropFilter: "blur(3px)",
};
const MODAL_BASE = {
  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
  background: "var(--bg-card)", border: "1px solid var(--border-card)",
  borderRadius: 12, padding: "24px 24px 20px", zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)", maxHeight: "90vh", overflowY: "auto",
};
const LABEL_STYLE = {
  display: "block", color: "var(--text-muted)", fontSize: "0.7rem", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
};
const INPUT_STYLE = {
  width: "100%", padding: "8px 10px", background: "var(--bg-input)",
  border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-primary)",
  fontSize: "0.85rem", outline: "none", boxSizing: "border-box", colorScheme: "light dark",
};

function Divider() {
  return <div style={{ borderTop: "1px solid var(--border-card)", margin: "16px 0" }} />;
}
function CloseBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: "none", border: "none", color: "var(--text-muted)",
      cursor: "pointer", fontSize: "1.15rem", lineHeight: 1, padding: "2px 4px",
    }}>✕</button>
  );
}
function Field({ label, required, children }) {
  return (
    <div>
      <label style={LABEL_STYLE}>{label}{required && <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>}</label>
      {children}
    </div>
  );
}
function PrimaryBtn({ children, onClick, disabled, color = "#f97316" }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "8px 18px", background: disabled ? `${color}66` : color, color: "#fff",
      border: "none", borderRadius: 7, fontSize: "0.84rem", fontWeight: 400,
      cursor: disabled ? "not-allowed" : "pointer", display: "flex",
      alignItems: "center", gap: 7,
    }}>{children}</button>
  );
}
function GhostBtn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "8px 16px", background: "transparent", border: "1px solid var(--border-card)",
      borderRadius: 7, color: "var(--text-muted)", fontSize: "0.84rem",
      cursor: disabled ? "not-allowed" : "pointer",
    }}>{children}</button>
  );
}

function ModalShell({ icon, title, subtitle, onClose, children, width = 560 }) {
  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY} />
      <div style={{ ...MODAL_BASE, width: `min(${width}px, 94vw)` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ background: "rgba(249,115,22,0.15)", borderRadius: 8, padding: "8px 10px", color: "#f97316" }}>
              <i className={`fa-solid ${icon}`} style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>{title}</h3>
              {subtitle && <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>{subtitle}</p>}
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>
        <Divider />
        {children}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Cálculos de cada préstamo (estado, próxima cuota, días)
// ─────────────────────────────────────────────────────────
// Cada tarjeta lleva un color distinto para distinguirlas de un vistazo; el
// estado (Vencido, Atrasado, Al día) va en la etiqueta.
const ACENTOS = ["orange", "green", "purple", "blue"];

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Días de `desde` a `hasta` (fechas ISO); negativo si `hasta` ya pasó. */
function diasEntre(desde, hasta) {
  return Math.round((new Date(`${hasta}T00:00:00`) - new Date(`${desde}T00:00:00`)) / 86400000);
}

/** El día `dia` del mes (año `y`, mes `m` 0-11, puede pasarse de 11) o el último día si el mes es más corto. */
function cuotaEnMes(y, m, dia) {
  const ultimo = new Date(y, m + 1, 0).getDate();
  const f = new Date(y, m, Math.min(dia, ultimo));
  return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}-${String(f.getDate()).padStart(2, "0")}`;
}

const saldoDe = (p) => Number(p.saldo ?? p.monto_inicial);

/**
 * Próxima cuota estimada (el préstamo no guarda un calendario de cuotas). Se
 * paga el mismo día del mes que la fecha de inicio:
 *  · con pagos registrados: la del mes siguiente al último pago; si esa fecha
 *    ya pasó, el préstamo está atrasado.
 *  · sin pagos registrados: la próxima desde hoy. Un préstamo que se registró
 *    con su saldo actual (sin cargar los pagos viejos) no sale atrasado.
 * Nunca después del vencimiento.
 */
function proximaCuota(p, hoy) {
  if (!p.activo || saldoDe(p) <= 0 || !p.fecha_inicio) return null;
  const dia = Number(p.fecha_inicio.slice(8, 10));
  const ultimoPago = (p.pagos || []).map((x) => x.fecha_pago).filter(Boolean).sort().pop();
  let fecha;
  if (ultimoPago) {
    const [y, m] = ultimoPago.split("-").map(Number);
    fecha = cuotaEnMes(y, m, dia);                       // m es 1-12: así cae en el mes siguiente
  } else {
    const [y, m] = hoy.split("-").map(Number);
    fecha = cuotaEnMes(y, m - 1, dia);
    if (fecha < hoy) fecha = cuotaEnMes(y, m, dia);
    if (fecha <= p.fecha_inicio) {
      const [yi, mi] = p.fecha_inicio.split("-").map(Number);
      fecha = cuotaEnMes(yi, mi, dia);
    }
  }
  return p.fecha_vencimiento && fecha > p.fecha_vencimiento ? p.fecha_vencimiento : fecha;
}

function estadoDe(p, hoy) {
  if (!p.activo) return { texto: "Inactivo", acento: "gray" };
  if (saldoDe(p) <= 0) return { texto: "Cancelado", acento: "gray" };
  if (p.fecha_vencimiento && p.fecha_vencimiento < hoy) return { texto: "Vencido", acento: "red" };
  const prox = proximaCuota(p, hoy);
  if (prox && prox < hoy) return { texto: "Atrasado", acento: "amber" };
  return { texto: "Al día", acento: "green" };
}

/** Suma por moneda → ["₡1,000.00"] o ["₡1,000.00", "$50.00"] (colones primero). */
function montoMulti(porMoneda) {
  const partes = MONEDAS.filter((m) => porMoneda[m])
    .map((m) => `${MONEDA_SYMBOL[m] ?? m}${formatCurrency(porMoneda[m])}`);
  return partes.length ? partes : ["₡0.00"];
}

/** Un monto por moneda: el primero grande, los demás en una línea chica abajo. */
function Montos({ lista, className }) {
  const [primero, ...resto] = Array.isArray(lista) ? lista : [lista];
  return (
    <>
      <span className={className} style={{ display: "block" }} title={String(primero)}>{primero}</span>
      {resto.length > 0 && <span className={fs.valueExtra}>+ {resto.join(" + ")}</span>}
    </>
  );
}
function sumarPorMoneda(lista, fn) {
  return lista.reduce((acc, p) => ({ ...acc, [p.moneda]: (acc[p.moneda] || 0) + fn(p) }), {});
}

// ─────────────────────────────────────────────────────────
// Tarjeta KPI
// ─────────────────────────────────────────────────────────
function Kpi({ acento, icon, label, value, sub }) {
  return (
    <div className={`${fs.kpi} ${fs[acento]}`}>
      <div className={fs.kpiIcon}><i className={`fa-solid ${icon}`} /></div>
      <div className={fs.kpiBody}>
        <p className={fs.kpiLabel}>{label}</p>
        <div style={{ margin: "4px 0 3px" }}><Montos lista={value} className={fs.kpiValue} /></div>
        <p className={fs.kpiSub}>{sub}</p>
      </div>
      <i className={`fa-solid ${icon} ${fs.kpiDeco}`} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Tarjeta de un préstamo
// ─────────────────────────────────────────────────────────
function PrestamoCard({ p, acento, hoy, onVerPagos, onPago, onEditar, onEliminar }) {
  const sym = MONEDA_SYMBOL[p.moneda] ?? p.moneda;
  const saldo = saldoDe(p);
  const inicial = Number(p.monto_inicial) || 1;
  const pctPagado = Math.min(100, Math.max(0, ((inicial - saldo) / inicial) * 100));
  const estado = estadoDe(p, hoy);
  const dias = p.fecha_vencimiento ? diasEntre(hoy, p.fecha_vencimiento) : null;

  return (
    <div className={`${fs.card} ${fs[acento]}`}>
      <div className={fs.cardHead}>
        <div className={fs.cardIcon}><i className="fa-solid fa-hand-holding-dollar" /></div>
        <div className={fs.cardTitleBox}>
          <div className={fs.cardTitle}>
            <span>{p.nombre}</span>
            <span className={`${fs.badge} ${fs.badgeUpper} ${fs[estado.acento]}`}>{estado.texto}</span>
          </div>
          <div className={fs.cardSub}>{p.entidad_financiera || "Sin entidad"}</div>
        </div>
      </div>

      <div className={fs.progressTop}>
        <span className={fs.progressLabel}>Saldo actual</span>
        <span className={fs.saldoValue} style={{ fontSize: "1.2rem" }}>{sym}{formatCurrency(saldo)}</span>
      </div>
      <div className={fs.progressTrack}>
        <div className={fs.progressFill} style={{ width: `${pctPagado}%` }} />
      </div>
      <div className={fs.progressPct}>{pctPagado.toFixed(1)}% pagado</div>

      <div className={fs.stats}>
        <div className={fs.stat}>
          <i className="fa-solid fa-percent" />
          <div style={{ minWidth: 0 }}>
            <div className={fs.statLabel}>Tasa anual</div>
            <div className={fs.statValue}>{Number(p.tasa_interes).toFixed(2)}%</div>
          </div>
        </div>
        <div className={fs.stat}>
          <i className="fa-solid fa-coins" />
          <div style={{ minWidth: 0 }}>
            <div className={fs.statLabel}>Cuota mensual</div>
            <div className={fs.statValue}>{sym}{formatCurrency(p.cuota_mensual)}</div>
          </div>
        </div>
        <div className={fs.stat}>
          <i className="fa-regular fa-calendar" />
          <div style={{ minWidth: 0 }}>
            <div className={fs.statLabel}>Vence</div>
            <div className={fs.statValue}>{fmtDate(p.fecha_vencimiento)}</div>
          </div>
        </div>
        <div className={fs.stat}>
          <i className="fa-regular fa-clock" />
          <div style={{ minWidth: 0 }}>
            <div className={fs.statLabel}>Días restantes</div>
            <div className={`${fs.statValue} ${fs.ok} ${fs[dias !== null && dias < 0 ? "red" : "green"]}`}>
              {dias === null ? "-" : dias < 0 ? "Vencido" : `${dias} ${dias === 1 ? "día" : "días"}`}
            </div>
          </div>
        </div>
      </div>

      <div className={`${fs.actions} ${fs.stacked}`}>
        <button type="button" className={fs.action} onClick={onVerPagos} title="Historial de pagos">
          <i className="fa-solid fa-clock-rotate-left" />Historial
        </button>
        <button type="button" className={fs.action} onClick={onPago} title="Registrar pago de cuota">
          <i className="fa-solid fa-money-check-dollar" />Pagar
        </button>
        <button type="button" className={fs.action} onClick={onEditar}>
          <i className="fa-solid fa-pencil" />Editar
        </button>
        <button type="button" className={`${fs.action} ${fs.danger}`} onClick={onEliminar}>
          <i className="fa-solid fa-trash" />Eliminar
        </button>
      </div>
    </div>
  );
}

// Fila de la vista lista
function PrestamoRow({ p, acento, hoy, onVerPagos, onPago, onEditar, onEliminar }) {
  const sym = MONEDA_SYMBOL[p.moneda] ?? p.moneda;
  const estado = estadoDe(p, hoy);
  return (
    <div className={`${fs.listRow} ${fs[acento]}`}>
      <div className={fs.cardIcon}><i className="fa-solid fa-hand-holding-dollar" /></div>
      <div className={fs.listMain}>
        <strong>{p.nombre}</strong>
        <small style={{ fontFamily: "inherit" }}>{p.entidad_financiera || "Sin entidad"} · Vence {fmtDate(p.fecha_vencimiento)}</small>
      </div>
      <span className={`${fs.badge} ${fs.badgeUpper} ${fs[estado.acento]}`}>{estado.texto}</span>
      <div className={fs.listAmount}>{sym}{formatCurrency(saldoDe(p))}</div>
      <div className={fs.iconBtns}>
        <button type="button" className={fs.iconBtn} title="Historial de pagos" onClick={onVerPagos}><i className="fa-solid fa-clock-rotate-left" /></button>
        <button type="button" className={fs.iconBtn} title="Registrar pago" onClick={onPago}><i className="fa-solid fa-money-check-dollar" /></button>
        <button type="button" className={fs.iconBtn} title="Editar" onClick={onEditar}><i className="fa-solid fa-pencil" /></button>
        <button type="button" className={`${fs.iconBtn} ${fs.danger}`} title="Eliminar" onClick={onEliminar}><i className="fa-solid fa-trash" /></button>
      </div>
    </div>
  );
}

function actionBtn(onClick, bg, border, color, icon, title) {
  return (
    <button type="button" onClick={onClick} title={title} style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 6, color,
      width: 30, height: 30, display: "inline-flex", alignItems: "center",
      justifyContent: "center", cursor: "pointer", fontSize: "0.8rem",
    }}>
      <i className={`fa-solid ${icon}`} />
    </button>
  );
}

// ─────────────────────────────────────────────────────────
// Página principal
// ─────────────────────────────────────────────────────────
export default function ListPrestamos() {
  const { prestamos, loading, create, update, remove, registrarPago, eliminarPago } = usePrestamos();
  const { cuentas } = useCuentas();

  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_PRESTAMO);
  const [saving, setSaving] = useState(false);

  const [editando, setEditando] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_PRESTAMO);

  const [pagando, setPagando] = useState(null);
  const [pagoForm, setPagoForm] = useState(EMPTY_PAGO);
  const [pagoSaving, setPagoSaving] = useState(false);

  const [viendoPagos, setViendoPagos] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [confirmandoPago, setConfirmandoPago] = useState(null);
  const [deletingPago, setDeletingPago] = useState(false);

  const [actionMsg, setActionMsg] = useState(null);

  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid"); // grid | list
  const [verTodosPagos, setVerTodosPagos] = useState(false);

  // Mantiene el modal "Historial de Pagos" sincronizado tras eliminar un pago
  // (usePrestamos recarga la lista completa; viendoPagos guarda una referencia
  // puntual que hay que refrescar con el objeto actualizado).
  useEffect(() => {
    if (!viendoPagos) return;
    const actualizado = prestamos.find((p) => p.id === viendoPagos.id);
    if (actualizado && actualizado !== viendoPagos) setViendoPagos(actualizado);
    if (!actualizado) setViendoPagos(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestamos]);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function handleCrear() {
    setSaving(true);
    try {
      await create(form);
      setForm(EMPTY_PRESTAMO);
      setNuevoOpen(false);
      showMsg(true, "Préstamo creado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleEditar() {
    setSaving(true);
    try {
      await update(editando.id, editForm);
      setEditando(null);
      showMsg(true, "Préstamo actualizado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePago() {
    setPagoSaving(true);
    try {
      await registrarPago(pagando.id, pagoForm);
      setPagando(null);
      showMsg(true, "Pago registrado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setPagoSaving(false);
    }
  }

  async function handleEliminar() {
    setDeleting(true);
    try {
      await remove(confirmando.id);
      setConfirmando(null);
      showMsg(true, "Préstamo eliminado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleEliminarPago() {
    setDeletingPago(true);
    try {
      await eliminarPago(confirmandoPago.id);
      setConfirmandoPago(null);
      showMsg(true, "Pago eliminado correctamente.");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDeletingPago(false);
    }
  }

  // ── Stats ────────────────────────────────────────────────────────────────
  // Por moneda: un préstamo en dólares no se suma como si fueran colones.
  const hoy = hoyISO();
  const activos = prestamos.filter((p) => p.activo);
  const deudaPorMoneda = sumarPorMoneda(activos, saldoDe);
  const pagadoPorMoneda = sumarPorMoneda(activos, (p) => Number(p.monto_inicial) - saldoDe(p));
  // El próximo que todavía no venció (si todos vencieron, el más antiguo).
  const vencimientos = activos.map((p) => p.fecha_vencimiento).filter(Boolean).sort();
  const proximoVencimiento = vencimientos.find((f) => f >= hoy) || vencimientos[0];
  const conAtraso = activos.filter((p) => ["Vencido", "Atrasado"].includes(estadoDe(p, hoy).texto));
  const proximos = activos
    .map((p) => ({ p, fecha: proximaCuota(p, hoy) }))
    .filter((x) => x.fecha)
    .sort((a, b) => a.fecha.localeCompare(b.fecha));

  const q = search.trim().toLowerCase();
  const filtrados = !q ? prestamos : prestamos.filter((p) =>
    (p.nombre || "").toLowerCase().includes(q) || (p.entidad_financiera || "").toLowerCase().includes(q));
  // El color de cada préstamo sigue su posición en la lista completa, así no
  // cambia al buscar.
  const acentoDe = (p) => ACENTOS[prestamos.indexOf(p) % ACENTOS.length];

  const acciones = (p) => ({
    onVerPagos: () => setViendoPagos(p),
    onPago: () => { setPagoForm(EMPTY_PAGO); setPagando(p); },
    onEditar: () => {
      setEditForm({
        nombre: p.nombre, entidad_financiera: p.entidad_financiera || "",
        tasa_interes: p.tasa_interes, cuota_mensual: p.cuota_mensual,
        fecha_vencimiento: p.fecha_vencimiento,
      });
      setEditando(p);
    },
    onEliminar: () => setConfirmando(p),
  });

  return (
    <div className={fs.page}>
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>Inicio /</span></Link>
        <span className={invoiceStyles.active}> Préstamos</span>
      </span>
      <div className={fs.header}>
        <div>
          <h2 className="p-0 title">Préstamos</h2>
          <p>Deuda bancaria - préstamos y leasings vigentes, con su saldo y pagos de cuota.</p>
        </div>
        <button type="button" className={fs.btnPrimary} onClick={() => { setForm(EMPTY_PRESTAMO); setNuevoOpen(true); }}>
          <i className="fa-solid fa-plus" />Nuevo préstamo
        </button>
      </div>

      <div className={fs.kpiGrid}>
        <Kpi acento="orange" icon="fa-building-columns" label="Deuda total" value={montoMulti(deudaPorMoneda)}
          sub={`${activos.length} préstamo(s) activo(s)`} />
        <Kpi acento="green" icon="fa-coins" label="Total pagado" value={montoMulti(pagadoPorMoneda)}
          sub="Capital amortizado" />
        <Kpi acento="purple" icon="fa-file-invoice-dollar" label="Préstamos activos" value={activos.length}
          sub={`de ${prestamos.length} en total`} />
        <Kpi acento="amber" icon="fa-calendar-day" label="Próximo vencimiento"
          value={proximoVencimiento ? fmtDate(proximoVencimiento) : "-"} sub="Entre los préstamos activos" />
      </div>

      {/* ── Mis préstamos ─────────────────────────────────────────────────── */}
      <div className={fs.panel}>
        <div className={fs.panelHead}>
          <div>
            <h3 className={fs.panelTitle}>Mis préstamos</h3>
            <p className={fs.panelSub}>Administrá tus préstamos y leasings registrados.</p>
          </div>
          {!loading && prestamos.length > 0 && (
            <div className={fs.tools}>
              <div className={fs.search}>
                <i className="fa-solid fa-magnifying-glass" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar préstamo…" />
              </div>
              <div className={fs.toggle}>
                <button type="button" title="Vista de tarjetas" className={view === "grid" ? fs.on : ""} onClick={() => setView("grid")}>
                  <i className="fa-solid fa-table-cells-large" />
                </button>
                <button type="button" title="Vista de lista" className={view === "list" ? fs.on : ""} onClick={() => setView("list")}>
                  <i className="fa-solid fa-list" />
                </button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className={fs.empty}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ opacity: 1, color: "var(--brand)", fontSize: "1.4rem" }} />Cargando…
          </div>
        ) : prestamos.length === 0 ? (
          <div className={fs.empty}>
            <i className="fa-solid fa-hand-holding-dollar" />
            No hay préstamos registrados.
          </div>
        ) : filtrados.length === 0 ? (
          <div className={fs.empty}>
            <i className="fa-solid fa-magnifying-glass" />
            Ningún préstamo coincide con "{search}".
          </div>
        ) : view === "grid" ? (
          <div className={fs.cardGrid} style={{ "--card-min": "285px" }}>
            {filtrados.map((p) => <PrestamoCard key={p.id} p={p} hoy={hoy} acento={acentoDe(p)} {...acciones(p)} />)}
          </div>
        ) : (
          <div className={fs.list}>
            {filtrados.map((p) => <PrestamoRow key={p.id} p={p} hoy={hoy} acento={acentoDe(p)} {...acciones(p)} />)}
          </div>
        )}
      </div>

      {/* ── Resumen + próximos pagos ──────────────────────────────────────── */}
      {!loading && prestamos.length > 0 && (
        <div className={fs.bottom}>
          <div className={fs.panel}>
            <h3 className={fs.panelTitle}>Resumen de préstamos</h3>
            <p className={fs.panelSub} style={{ marginBottom: 14 }}>Vista rápida del estado de tus préstamos.</p>
            <div className={fs.strip}>
              <div className={`${fs.stripItem} ${fs.green}`}>
                <div className={fs.stripIcon}><i className="fa-solid fa-file-lines" /></div>
                <div>
                  <div className={fs.stripValue}>{activos.length}</div>
                  <div className={fs.stripLabel}>Préstamos activos</div>
                </div>
              </div>
              <div className={`${fs.stripItem} ${fs.orange}`}>
                <div className={fs.stripIcon}><i className="fa-solid fa-building-columns" /></div>
                <div style={{ minWidth: 0 }}>
                  <Montos lista={montoMulti(deudaPorMoneda)} className={fs.stripValue} />
                  <div className={fs.stripLabel}>Deuda total</div>
                </div>
              </div>
              <div className={`${fs.stripItem} ${fs.purple}`}>
                <div className={fs.stripIcon}><i className="fa-solid fa-coins" /></div>
                <div style={{ minWidth: 0 }}>
                  <Montos lista={montoMulti(pagadoPorMoneda)} className={fs.stripValue} />
                  <div className={fs.stripLabel}>Total pagado</div>
                </div>
              </div>
              <div className={`${fs.stripItem} ${fs.red}`}>
                <div className={fs.stripIcon}><i className="fa-solid fa-circle-exclamation" /></div>
                <div>
                  <div className={fs.stripValue}>{conAtraso.length}</div>
                  <div className={fs.stripLabel}>Vencidos o atrasados</div>
                </div>
              </div>
            </div>
          </div>

          <div className={fs.panel}>
            <div className={fs.panelHead} style={{ marginBottom: 12 }}>
              <div>
                <h3 className={fs.panelTitle}>Próximos pagos</h3>
                <p className={fs.panelSub}>Próxima cuota estimada de cada préstamo activo.</p>
              </div>
              {proximos.length > 4 && (
                <button type="button" className={fs.linkBtn} onClick={() => setVerTodosPagos((v) => !v)}>
                  {verTodosPagos ? "Ver menos" : "Ver todos"} <i className={`fa-solid ${verTodosPagos ? "fa-chevron-up" : "fa-arrow-right"}`} />
                </button>
              )}
            </div>
            {proximos.length === 0 ? (
              <div className={fs.empty} style={{ padding: 24 }}>No hay cuotas pendientes.</div>
            ) : (verTodosPagos ? proximos : proximos.slice(0, 4)).map(({ p, fecha }) => {
              const atrasada = fecha < hoy;
              const dias = diasEntre(hoy, fecha);
              return (
                <div key={p.id} className={`${fs.nextRow} ${fs[atrasada ? "red" : acentoDe(p)]}`}>
                  <div className={fs.nextIcon}><i className="fa-regular fa-calendar" /></div>
                  <div className={fs.nextName}>
                    <strong>{p.nombre}</strong>
                    <small>{p.entidad_financiera || "Sin entidad"}</small>
                  </div>
                  <div className={fs.nextDate}>
                    {fmtDate(fecha)}
                    <div style={{ marginTop: 3 }}>
                      <span className={`${fs.badge} ${fs[atrasada ? "red" : dias <= 7 ? "amber" : "green"]}`}>
                        {atrasada ? "Atrasada" : dias === 0 ? "Hoy" : `En ${dias} ${dias === 1 ? "día" : "días"}`}
                      </span>
                    </div>
                  </div>
                  <div className={fs.nextAmount}>{MONEDA_SYMBOL[p.moneda] ?? p.moneda}{formatCurrency(p.cuota_mensual)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Modal: Nuevo préstamo ─────────────────────────────────────────── */}
      {nuevoOpen && (
        <ModalShell icon="fa-hand-holding-dollar" title="Nuevo Préstamo"
          subtitle="Registra un préstamo o leasing con una entidad financiera"
          onClose={() => setNuevoOpen(false)} width={620}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", marginBottom: 18 }}>
            <Field label="Nombre" required>
              <input style={INPUT_STYLE} placeholder="Ej: Operación 1" value={form.nombre}
                onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
            </Field>
            <Field label="Entidad Financiera">
              <input style={INPUT_STYLE} placeholder="Ej: Banco Nacional" value={form.entidad_financiera}
                onChange={(e) => setForm((f) => ({ ...f, entidad_financiera: e.target.value }))} />
            </Field>
            <Field label="Cuenta Bancaria" required>
              <select style={{ ...INPUT_STYLE, cursor: "pointer" }} value={form.cuenta_bancaria}
                onChange={(e) => setForm((f) => ({ ...f, cuenta_bancaria: e.target.value }))}>
                <option value="">Seleccione…</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco} - {c.numero_cuenta}</option>)}
              </select>
            </Field>
            <Field label="Monto Inicial" required>
              <input style={INPUT_STYLE} type="number" value={form.monto_inicial}
                onChange={(e) => setForm((f) => ({ ...f, monto_inicial: e.target.value }))} />
            </Field>
            <Field label="Moneda">
              <select style={{ ...INPUT_STYLE, cursor: "pointer" }} value={form.moneda}
                onChange={(e) => setForm((f) => ({ ...f, moneda: e.target.value }))}>
                {MONEDAS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Tasa Interés Anual (%)" required>
              <input style={INPUT_STYLE} type="number" step="0.001" value={form.tasa_interes}
                onChange={(e) => setForm((f) => ({ ...f, tasa_interes: e.target.value }))} />
            </Field>
            <Field label="Cuota Mensual" required>
              <input style={INPUT_STYLE} type="number" value={form.cuota_mensual}
                onChange={(e) => setForm((f) => ({ ...f, cuota_mensual: e.target.value }))} />
            </Field>
            <Field label="Fecha Inicio" required>
              <input style={INPUT_STYLE} type="date" value={form.fecha_inicio}
                onChange={(e) => setForm((f) => ({ ...f, fecha_inicio: e.target.value }))} />
            </Field>
            <Field label="Fecha Vencimiento" required>
              <input style={INPUT_STYLE} type="date" value={form.fecha_vencimiento}
                onChange={(e) => setForm((f) => ({ ...f, fecha_vencimiento: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <GhostBtn onClick={() => setNuevoOpen(false)} disabled={saving}>Cancelar</GhostBtn>
            <PrimaryBtn onClick={handleCrear} disabled={saving}>
              {saving ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</> : <><i className="fa-solid fa-floppy-disk" />Guardar préstamo</>}
            </PrimaryBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Modal: Editar préstamo ────────────────────────────────────────── */}
      {editando && (
        <ModalShell icon="fa-pencil" title="Editar Préstamo" subtitle={editando.nombre}
          onClose={() => setEditando(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", marginBottom: 18 }}>
            <Field label="Nombre">
              <input style={INPUT_STYLE} value={editForm.nombre}
                onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} />
            </Field>
            <Field label="Entidad Financiera">
              <input style={INPUT_STYLE} value={editForm.entidad_financiera || ""}
                onChange={(e) => setEditForm((f) => ({ ...f, entidad_financiera: e.target.value }))} />
            </Field>
            <Field label="Tasa Interés (%)">
              <input style={INPUT_STYLE} type="number" step="0.001" value={editForm.tasa_interes}
                onChange={(e) => setEditForm((f) => ({ ...f, tasa_interes: e.target.value }))} />
            </Field>
            <Field label="Cuota Mensual">
              <input style={INPUT_STYLE} type="number" value={editForm.cuota_mensual}
                onChange={(e) => setEditForm((f) => ({ ...f, cuota_mensual: e.target.value }))} />
            </Field>
            <Field label="Fecha Vencimiento">
              <input style={INPUT_STYLE} type="date" value={editForm.fecha_vencimiento}
                onChange={(e) => setEditForm((f) => ({ ...f, fecha_vencimiento: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <GhostBtn onClick={() => setEditando(null)} disabled={saving}>Cancelar</GhostBtn>
            <PrimaryBtn onClick={handleEditar} disabled={saving}>
              {saving ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</> : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </PrimaryBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Modal: Registrar pago ─────────────────────────────────────────── */}
      {pagando && (
        <ModalShell icon="fa-money-check-dollar" title="Registrar Pago de Cuota" subtitle={pagando.nombre}
          onClose={() => setPagando(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px", marginBottom: 18 }}>
            <Field label="Fecha" required>
              <input style={INPUT_STYLE} type="date" value={pagoForm.fecha_pago}
                onChange={(e) => setPagoForm((f) => ({ ...f, fecha_pago: e.target.value }))} />
            </Field>
            <Field label="Documento">
              <input style={INPUT_STYLE} value={pagoForm.documento}
                onChange={(e) => setPagoForm((f) => ({ ...f, documento: e.target.value }))} />
            </Field>
            <Field label="Monto Capital" required>
              <input style={INPUT_STYLE} type="number" value={pagoForm.monto_capital}
                onChange={(e) => setPagoForm((f) => ({ ...f, monto_capital: e.target.value }))} />
            </Field>
            <Field label="Monto Interés" required>
              <input style={INPUT_STYLE} type="number" value={pagoForm.monto_interes}
                onChange={(e) => setPagoForm((f) => ({ ...f, monto_interes: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <GhostBtn onClick={() => setPagando(null)} disabled={pagoSaving}>Cancelar</GhostBtn>
            <PrimaryBtn onClick={handlePago} disabled={pagoSaving} color="#4ade80">
              {pagoSaving ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</> : <><i className="fa-solid fa-floppy-disk" />Guardar pago</>}
            </PrimaryBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Modal: Historial de pagos ─────────────────────────────────────── */}
      {viendoPagos && (
        <ModalShell icon="fa-clock-rotate-left" title="Historial de Pagos" subtitle={viendoPagos.nombre}
          onClose={() => setViendoPagos(null)} width={640}>
          {!viendoPagos.pagos?.length ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)", fontSize: "0.85rem" }}>
              Aún no hay pagos registrados para este préstamo.
            </div>
          ) : (
            <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
              <table>
                <thead>
                  <tr>
                    <th>N° Pago</th>
                    <th>Fecha</th>
                    <th>Capital</th>
                    <th>Interés</th>
                    <th>Documento</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {viendoPagos.pagos.map((pago) => (
                    <tr key={pago.id}>
                      <td>{pago.numero_pago}</td>
                      <td>{fmtDate(pago.fecha_pago)}</td>
                      <td>{formatCurrency(pago.monto_capital)}</td>
                      <td>{formatCurrency(pago.monto_interes)}</td>
                      <td>{pago.documento || "-"}</td>
                      <td className="text-center">
                        {actionBtn(() => setConfirmandoPago(pago), "rgba(239,68,68,0.1)", "rgba(239,68,68,0.25)", "#f87171", "fa-trash", "Eliminar pago")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
            <GhostBtn onClick={() => setViendoPagos(null)}>Cerrar</GhostBtn>
          </div>
        </ModalShell>
      )}

      {/* ── Confirmar eliminación ─────────────────────────────────────────── */}
      {confirmando && (
        <ConfirmModal
          title="Eliminar Préstamo"
          message={`¿Estás seguro de que deseas eliminar el préstamo "${confirmando.nombre}"? Esta acción no se puede deshacer. Si tiene pagos registrados, quedará bloqueado.`}
          loading={deleting}
          onConfirm={handleEliminar}
          onCancel={() => setConfirmando(null)}
        />
      )}

      {/* ── Confirmar eliminación de un pago ──────────────────────────────── */}
      {confirmandoPago && (
        <ConfirmModal
          title="Eliminar Pago"
          message={`¿Estás seguro de que deseas eliminar el pago "${confirmandoPago.numero_pago}"? Esto revertirá el asiento contable y el movimiento bancario asociados (si existen). Esta acción no se puede deshacer.`}
          loading={deletingPago}
          onConfirm={handleEliminarPago}
          onCancel={() => setConfirmandoPago(null)}
        />
      )}

      {/* ── Toast flotante ────────────────────────────────────────────────── */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(251,146,60,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(251,146,60,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#fb923c"}`,
          borderRadius: 8, padding: "12px 18px", display: "flex", alignItems: "center",
          gap: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
            style={{ color: actionMsg.ok ? "#4ade80" : "#fb923c", fontSize: "1.1rem" }} />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#fb923c", fontWeight: 600, fontSize: "0.88rem" }}>
            {actionMsg.text}
          </span>
        </div>
      )}
    </div>
  );
}
